/**
 * The LiveKit room, as reactive state.
 *
 * LiveKit's Room is an event emitter holding mutable objects; Preact needs
 * immutable snapshots to render from. This hook is the seam: it subscribes to
 * the events that matter and republishes them as plain state.
 */

import { useCallback, useEffect, useRef, useState } from 'preact/hooks';
import {
  ConnectionState,
  DisconnectReason,
  type Participant,
  type RemoteParticipant,
  Room,
  RoomEvent,
  Track,
} from 'livekit-client';
import { parseReaction, type IncomingReaction } from './reactions.js';
import { haptic } from './haptics.js';

export interface StagePerson {
  identity: string;
  name: string;
  isLocal: boolean;
  isSpeaking: boolean;
  hasMic: boolean;
  isMuted: boolean;
  /** 0–1, for the speaking ring. */
  level: number;
  /**
   * Their live camera or screen share, if any.
   *
   * The Track itself rather than a boolean: attaching is what actually renders
   * it, and only the component holding the <video> element can do that.
   */
  videoTrack: Track | null;
  /** Screen share outranks camera — it is what the room is being shown. */
  videoSource: 'camera' | 'screen' | null;
}

export type Phase = 'idle' | 'connecting' | 'connected' | 'reconnecting' | 'disconnected';

export interface RoomState {
  phase: Phase;
  stage: StagePerson[];
  canSpeak: boolean;
  micOn: boolean;
  cameraOn: boolean;
  screenOn: boolean;
  error: string | null;
  /** Set when the server ejected us, so the UI can say so rather than just drop. */
  removed: boolean;
  /**
   * The browser is refusing to play audio until it sees a user gesture.
   *
   * This is the single most important piece of state in the product: when it is
   * true the room looks completely normal and is completely silent. The UI must
   * surface it, not swallow it.
   */
  audioBlocked: boolean;
}

const snapshot = (room: Room): StagePerson[] => {
  /*
   * The stage is people who can SPEAK, not everyone connected.
   *
   * Remote listeners never arrive here at all — they hold `hidden: true`, so
   * the SFU never announces them. But the local participant is always present
   * in the SDK, which meant a listener saw their own tile under "On stage" and
   * an empty room looked occupied. Filter on the publish grant rather than on
   * an existing mic track, so someone just promoted appears immediately instead
   * of only after they unmute.
   */
  const canPublish = (p: Participant) => (p.permissions?.canPublishSources?.length ?? 0) > 0;
  const people: Participant[] = [
    ...(canPublish(room.localParticipant) ? [room.localParticipant as Participant] : []),
    ...room.remoteParticipants.values(),
  ];
  return people
    .map((p) => {
      const mic = p.getTrackPublication(Track.Source.Microphone);
      /*
       * Screen share wins over camera. If someone is showing a clip, that IS
       * the show — their face is secondary and the grid has one slot per person.
       */
      const screen = p.getTrackPublication(Track.Source.ScreenShare);
      const camera = p.getTrackPublication(Track.Source.Camera);
      const video = screen?.track ? screen : camera;
      const videoTrack = video?.track && !video.isMuted ? (video.track as Track) : null;
      return {
        identity: p.identity,
        /*
         * Never fall back to the raw subject. `anon_speaker` is an internal id
         * and showing it in a room reads as a bug to everyone present.
         *
         * The suffix is uppercased so it reads as an identifier rather than as
         * a truncated word — lowercase `Guest aker` looks like a mistake, while
         * `Guest AKER` reads as a label. Their avatar colour does most of the
         * distinguishing anyway (see identity.ts).
         */
        name: p.name || `Guest ${p.identity.slice(-4).toUpperCase()}`,
        isLocal: p === room.localParticipant,
        hasMic: Boolean(mic),
        isMuted: Boolean(mic?.isMuted),
        isSpeaking: p.isSpeaking && Boolean(mic) && !mic?.isMuted,
        level: p.audioLevel ?? 0,
        videoTrack,
        videoSource: (videoTrack ? (video === screen ? 'screen' : 'camera') : null) as
          | 'camera'
          | 'screen'
          | null,
      };
    })
    /*
     * Video first, then speakers, then alphabetical. Stable within each group,
     * so tiles do not reshuffle every time an audio level crosses a threshold
     * mid-sentence.
     */
    .sort(
      (a, b) =>
        Number(Boolean(b.videoTrack)) - Number(Boolean(a.videoTrack)) ||
        Number(b.hasMic) - Number(a.hasMic) ||
        a.name.localeCompare(b.name),
    );
};

export function useRoom(onReaction: (reaction: IncomingReaction) => void) {
  const roomRef = useRef<Room | null>(null);
  const reactionRef = useRef(onReaction);
  reactionRef.current = onReaction;

  const [state, setState] = useState<RoomState>({
    phase: 'idle',
    stage: [],
    canSpeak: false,
    micOn: false,
    cameraOn: false,
    screenOn: false,
    error: null,
    removed: false,
    audioBlocked: false,
  });

  /**
   * Join a room.
   *
   * `micTrack` is what "Go live" means: a host who taps it expects to be heard,
   * not to land on stage muted hunting for an Unmute button. The audience flow
   * omits it — a listener joins silently, and a guest promoted mid-show should
   * choose their own moment.
   *
   * It is a track rather than a boolean because the caller must open the
   * microphone inside the tap that started this (see primeMicrophone): by the
   * time we are here, two awaits have passed and iOS no longer treats the tap
   * as a gesture.
   */
  const connect = useCallback(async (
    url: string,
    token: string,
    canSpeak: boolean,
    options: { micTrack?: MediaStreamTrack } = {},
  ) => {
    setState((s) => ({ ...s, phase: 'connecting', error: null, removed: false }));

    const room = new Room({
      adaptiveStream: true,
      dynacast: true,
      // Audio-first product: ask for the settings a voice room wants rather
      // than the browser's video-call defaults.
      audioCaptureDefaults: {
        autoGainControl: true,
        echoCancellation: true,
        noiseSuppression: true,
      },
      /*
       * One mixed output instead of one <audio> element per speaker.
       *
       * Echo cancellation works by subtracting what we are PLAYING from what we
       * are HEARING, so it is only as good as its picture of the output. A pile
       * of independently scheduled elements is a moving target: each buffers on
       * its own clock, so the delay AEC has to estimate drifts per element and
       * the tail it fails to cancel is what comes back as echo. Mixed through a
       * single AudioContext there is one render graph on one clock, which is the
       * signal the canceller is designed to model.
       *
       * It also retires the per-element playback race that made this room silent
       * before: one context to start, not N elements to nurse.
       *
       * ROLLBACK: set this to false. That restores per-element playback exactly,
       * and nothing else in this file depends on it being on.
       */
      webAudioMix: true,
    });
    roomRef.current = room;

    const sync = () =>
      setState((s) => ({
        ...s,
        stage: snapshot(room),
        micOn: room.localParticipant.isMicrophoneEnabled,
        cameraOn: room.localParticipant.isCameraEnabled,
        screenOn: room.localParticipant.isScreenShareEnabled,
        canSpeak: (room.localParticipant.permissions?.canPublishSources?.length ?? 0) > 0,
      }));

    room
      .on(RoomEvent.Connected, () => {
        // THE MOMENT OF TRUTH (Constitution §6): the room connected.
        haptic('thump');
        setState((s) => ({ ...s, phase: 'connected', canSpeak }));
        sync();
      })
      .on(RoomEvent.Disconnected, (reason) => {
        setState((s) => ({
          ...s,
          phase: 'disconnected',
          removed: reason === DisconnectReason.PARTICIPANT_REMOVED,
        }));
      })
      .on(RoomEvent.Reconnecting, () => setState((s) => ({ ...s, phase: 'reconnecting' })))
      .on(RoomEvent.Reconnected, () => {
        setState((s) => ({ ...s, phase: 'connected' }));
        sync();
      })
      .on(RoomEvent.TrackSubscribed, (track) => {
        /*
         * Attaching is what makes a track audible, and audio playback is
         * position-independent: nobody sees the element, so one hidden <audio>
         * appended here is the whole playback path.
         *
         * Video is deliberately NOT attached here — see below.
         *
         * NOT `display: none`. A display:none media element is a documented
         * source of playback trouble (Safari in particular treats it as
         * non-rendered), and it also removes any chance of a native control as
         * a last resort. Hidden visually, still rendered.
         */
        if (track.kind === Track.Kind.Audio) {
          /*
           * IN THE DOM FIRST, then attach.
           *
           * `track.attach()` builds an element AND calls play() on it. Doing
           * that before the element is in the document means Safari is being
           * asked to play a detached node, which it refuses — and nothing
           * retried, so the room was silent with a healthy track on both sides.
           * Every server-side stage checked out; only this did not.
           */
          const element = document.createElement('audio');
          element.setAttribute('data-waves-audio', '');
          element.autoplay = true;
          // iOS refuses to play media it considers non-inline.
          element.setAttribute('playsinline', '');
          Object.assign(element.style, {
            position: 'absolute',
            width: '1px',
            height: '1px',
            opacity: '0',
            pointerEvents: 'none',
          });
          document.body.appendChild(element);

          /*
           * Still in the DOM before attach, even though the mixer now carries
           * the sound.
           *
           * attach() sets this element's srcObject, and the web audio graph is
           * built FROM that srcObject — so the element stays the source the
           * mixer reads, just muted rather than audible. Attaching a detached
           * node was the original silent-room bug, and this ordering is also
           * what keeps the fallback honest if webAudioMix is turned back off.
           */
          track.attach(element);

          /*
           * Nudge the CONTEXT, not the element.
           *
           * Under a mixed output the SDK deliberately mutes this element, so
           * play() on it can succeed while the room stays silent — precisely the
           * false all-clear that hid the last bug. The thing that can actually
           * be blocked now is the AudioContext, which iOS creates suspended and
           * only a real gesture may resume.
           */
          const nudge = async () => {
            if (room.canPlaybackAudio) return;
            try {
              await room.startAudio();
            } catch {
              /* SoundGate below is the visible fallback */
            }
          };
          void nudge();
          setTimeout(() => void nudge(), 400);
          setTimeout(() => void nudge(), 1500);
          // Re-read the gate: if playback is genuinely blocked, SoundGate has
          // to appear. Silence with no explanation is the failure this product
          // keeps having.
          setTimeout(
            () => setState((s) => ({ ...s, audioBlocked: !room.canPlaybackAudio })),
            1800,
          );
        }
        /*
         * Video is the opposite case: the element IS the interface, so only the
         * component that renders the tile can attach it. Attaching centrally
         * would mean an off-screen <video> per participant, and the browser
         * still runs a decoder for each one — on the cheap Android phones this
         * audience actually uses, that is battery and heat spent on pixels
         * nobody sees. VideoTile attaches and detaches it instead.
         */
        sync();
      })
      /*
       * THE AUTOPLAY GATE.
       *
       * Browsers refuse to play audio until the page has seen a user gesture.
       * LiveKit attaches the track happily either way, so without this the room
       * connects, shows the right people, shows them speaking — and is silent.
       * There is no error anywhere; it is the quietest possible failure.
       */
      .on(RoomEvent.AudioPlaybackStatusChanged, () => {
        setState((s) => ({ ...s, audioBlocked: !room.canPlaybackAudio }));
      })
      .on(RoomEvent.TrackUnsubscribed, (track) => {
        /*
         * Only tear down the elements THIS module created.
         *
         * A bare detach() returns every attached element, which would include
         * the <video> inside a VideoTile — and removing that node by hand rips
         * it out from under Preact, which still believes it is mounted. Video
         * detaches itself in the tile's effect cleanup when the track goes away.
         */
        track
          .detach()
          .filter((element) => element.hasAttribute('data-waves-audio'))
          .forEach((element) => element.remove());
        sync();
      })
      .on(RoomEvent.ParticipantConnected, sync)
      .on(RoomEvent.ParticipantDisconnected, sync)
      .on(RoomEvent.ActiveSpeakersChanged, sync)
      .on(RoomEvent.TrackMuted, sync)
      .on(RoomEvent.TrackUnmuted, sync)
      .on(RoomEvent.LocalTrackPublished, sync)
      .on(RoomEvent.LocalTrackUnpublished, sync)
      // The stage mechanic: the server changed our grants mid-session, so a
      // promoted listener gains a microphone without rejoining.
      .on(RoomEvent.ParticipantPermissionsChanged, () => {
        const sources = room.localParticipant.permissions?.canPublishSources ?? [];
        const nowSpeaks = sources.length > 0;
        setState((s) => {
          if (nowSpeaks && !s.canSpeak) haptic('press');
          return { ...s, canSpeak: nowSpeaks };
        });
        sync();
      })
      .on(RoomEvent.DataReceived, (payload, participant?: RemoteParticipant, kind?) => {
        const reaction = parseReaction(payload, participant?.identity, kind);
        if (reaction) reactionRef.current(reaction);
      });

    try {
      await room.connect(url, token);

      /*
       * Unlock playback while the join click is still the current user gesture.
       *
       * Called after connect rather than before because the gesture's validity
       * outlives the await, and calling it here means one attempt covers every
       * track that arrives later. If it still fails, `audioBlocked` drives a
       * visible control — never a silent room.
       */
      if (!room.canPlaybackAudio) {
        try {
          await room.startAudio();
        } catch {
          /* handled below and by AudioPlaybackStatusChanged */
        }
      }
      setState((s) => ({ ...s, audioBlocked: !room.canPlaybackAudio }));

      /*
       * Publish the microphone the caller already opened.
       *
       * `publishTrack` with an explicit source rather than setMicrophoneEnabled,
       * because that would call getUserMedia AGAIN — and on iOS a second
       * acquisition of a device just released yields a live, unmuted, silent
       * track. The source must be stated or the SFU will not match it against
       * the MICROPHONE grant.
       *
       * Last, deliberately: publishing before the room is connected throws.
       */
      if (options.micTrack) {
        try {
          await room.localParticipant.publishTrack(options.micTrack, {
            source: Track.Source.Microphone,
          });
        } catch (error) {
          // Ours to clean up: an unpublished track keeps the phone's recording
          // indicator lit for a call the user is not in.
          options.micTrack.stop();
          // Not fatal: they are in the room and can retry with the button. But
          // it must be SAID, or it reads as "the app has no sound".
          setState((s) => ({
            ...s,
            error:
              error instanceof Error && /denied|permission|NotAllowed/i.test(error.message)
                ? 'Microphone permission is blocked. Allow it in Settings, then tap Unmute.'
                : 'Could not open the microphone. Tap Unmute to try again.',
          }));
        }
        setState((s) => ({ ...s, micOn: room.localParticipant.isMicrophoneEnabled }));
      }
    } catch (error) {
      setState((s) => ({
        ...s,
        phase: 'disconnected',
        error: error instanceof Error ? error.message : String(error),
      }));
      throw error;
    }
  }, []);

  const toggleMic = useCallback(async () => {
    const room = roomRef.current;
    if (!room) return;
    const next = !room.localParticipant.isMicrophoneEnabled;
    haptic('tick');
    try {
      await room.localParticipant.setMicrophoneEnabled(next);
      setState((s) => ({ ...s, micOn: next, stage: snapshot(room) }));
    } catch (error) {
      // The SFU rejects publishing without the grant — a patched client cannot
      // talk its way onto the stage.
      haptic('snag');
      setState((s) => ({
        ...s,
        error: error instanceof Error ? error.message : 'Microphone unavailable',
      }));
    }
  }, []);

  /**
   * Retry playback from a real user gesture.
   *
   * Bound to a visible button, because the browser will only relent for an
   * event it can attribute to the person.
   */
  const enableAudio = useCallback(async () => {
    const room = roomRef.current;
    if (!room) return;
    haptic('press');
    try {
      await room.startAudio();
    } catch {
      /* leave audioBlocked true; the control stays on screen */
    }
    setState((s) => ({ ...s, audioBlocked: !room.canPlaybackAudio }));
  }, []);

  /**
   * Camera on/off.
   *
   * Separate from the mic because they are separate decisions on stage — a
   * comedian may want to be heard without being seen, and the grant model
   * already treats them as different sources.
   */
  const toggleCamera = useCallback(async () => {
    const room = roomRef.current;
    if (!room) return;
    const next = !room.localParticipant.isCameraEnabled;
    haptic('tick');
    try {
      await room.localParticipant.setCameraEnabled(next);
      setState((s) => ({ ...s, cameraOn: next, stage: snapshot(room) }));
    } catch (error) {
      haptic('snag');
      setState((s) => ({
        ...s,
        error: error instanceof Error ? error.message : 'Camera unavailable',
      }));
    }
  }, []);

  /**
   * Screen share.
   *
   * Desktop only in practice — mobile browsers do not implement
   * getDisplayMedia — so a failure here is expected on a phone and must read as
   * "not available", never as a crash.
   */
  const toggleScreen = useCallback(async () => {
    const room = roomRef.current;
    if (!room) return;
    const next = !room.localParticipant.isScreenShareEnabled;
    haptic('tick');
    try {
      await room.localParticipant.setScreenShareEnabled(next, { audio: true });
      setState((s) => ({ ...s, screenOn: next, stage: snapshot(room) }));
    } catch (error) {
      // A user dismissing the picker throws too; that is not an error worth
      // shouting about.
      const message = error instanceof Error ? error.message : '';
      if (!/permission|denied|abort|cancel/i.test(message)) {
        haptic('snag');
        setState((s) => ({ ...s, error: 'Screen sharing is not available on this device.' }));
      }
      setState((s) => ({ ...s, screenOn: room.localParticipant.isScreenShareEnabled }));
    }
  }, []);

  const leave = useCallback(async () => {
    await roomRef.current?.disconnect();
    roomRef.current = null;
    document.querySelectorAll('[data-waves-audio]').forEach((el) => el.remove());
    setState({
      phase: 'idle', stage: [], canSpeak: false,
      micOn: false, cameraOn: false, screenOn: false,
      error: null, removed: false, audioBlocked: false,
    });
  }, []);

  /* Audio levels change continuously but LiveKit only emits on threshold
     crossings, so the speaking ring is polled. 10 Hz is smooth to the eye and
     costs nothing next to decoding audio. */
  useEffect(() => {
    if (state.phase !== 'connected') return;
    const timer = setInterval(() => {
      const room = roomRef.current;
      if (room?.state === ConnectionState.Connected) {
        setState((s) => ({ ...s, stage: snapshot(room) }));
      }
    }, 100);
    return () => clearInterval(timer);
  }, [state.phase]);

  useEffect(() => () => void roomRef.current?.disconnect(), []);

  return {
    ...state, room: roomRef,
    connect, toggleMic, toggleCamera, toggleScreen, leave, enableAudio,
  };
}
